import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Loader2, Key, Copy, Trash2, UserCog } from 'lucide-react';
import { toast } from 'sonner';

interface RegulatorAccessManagerProps {
  companyId?: string;
}

export default function RegulatorAccessManager({ companyId }: RegulatorAccessManagerProps) {
  const [codes, setCodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [jurisdictionInput, setJurisdictionInput] = useState('');
  const [hours, setHours] = useState('168'); // 7 days
  const [creating, setCreating] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('regulator_access')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setCodes(data || []);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function createAccess() {
    if (!name || !email) return toast.error('Name and email are required');

    setCreating(true);
    try {
      const accessCode = crypto.randomUUID();
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + parseInt(hours));

      const { error } = await supabase.from('regulator_access').insert({
        access_code: accessCode,
        regulator_name: name,
        regulator_email: email,
        jurisdiction: jurisdictionInput || null,
        expires_at: expiresAt.toISOString(),
        created_by: (await supabase.auth.getUser()).data.user?.id,
      });

      if (error) throw error;

      const portalUrl = `${window.location.origin}/regulator-portal?code=${accessCode}`;
      await navigator.clipboard.writeText(portalUrl);

      toast.success('Access code created and URL copied to clipboard');
      setIsOpen(false);
      resetForm();
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  }

  async function revokeAccess(id: string) {
    try {
      const { error } = await supabase
        .from('regulator_access')
        .update({ active: false })
        .eq('id', id);
      if (error) throw error;
      toast.success('Access revoked');
      load();
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  function resetForm() {
    setName('');
    setEmail('');
    setJurisdictionInput('');
    setHours('168');
  }

  function copyUrl(code: string) {
    const url = `${window.location.origin}/regulator-portal?code=${code}`;
    navigator.clipboard.writeText(url);
    toast.success('Portal URL copied');
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <UserCog className="h-5 w-5 text-primary" />
          Regulator Access Management
        </CardTitle>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Key className="h-4 w-4 mr-1" /> Issue Access
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Issue Regulator Access</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label htmlFor="name">Regulator Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., CIPC Auditor"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="regulator@example.com"
                />
              </div>
              <div>
                <Label htmlFor="jurisdiction">Jurisdiction (Optional)</Label>
                <Input
                  id="jurisdiction"
                  value={jurisdictionInput}
                  onChange={(e) => setJurisdictionInput(e.target.value)}
                  placeholder="e.g., ZA"
                />
              </div>
              <div>
                <Label htmlFor="hours">Valid For (hours)</Label>
                <Input
                  id="hours"
                  type="number"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  placeholder="168"
                />
              </div>
              <Button onClick={createAccess} disabled={creating} className="w-full">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Access'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="p-6 flex justify-center">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        ) : codes.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Regulator</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Jurisdiction</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {codes.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.regulator_name}</TableCell>
                  <TableCell>{c.regulator_email}</TableCell>
                  <TableCell>{c.jurisdiction || '—'}</TableCell>
                  <TableCell>{new Date(c.expires_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {c.active && new Date(c.expires_at) > new Date() ? (
                      <span className="text-green-600">Active</span>
                    ) : (
                      <span className="text-muted-foreground">Inactive</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button variant="ghost" size="sm" onClick={() => copyUrl(c.access_code)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => revokeAccess(c.id)}
                      disabled={!c.active}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">
            No regulator access codes issued yet.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
